import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { fetchCustomerMovements, getCustomerAccountById, getCustomerMovementHistory, getCustomerSalesForCustomer, recordCustomerCharge, recordCustomerPayment } from '../../infra/customerRepository'
import { cancelCustomerPaymentIntent, createCustomerPaymentIntent } from '../../infra/customerPaymentIntents';
import { useCustomerPaymentQrSession } from './useCustomerPaymentQrSession';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import { useGlobalFeatureFlags } from '../../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../../shell/feature-flags/globalFeatureFlagsService';
import { getCachedMpIntegrationStatus } from '../../../settings/application/storefrontStatus';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import type { CustomerAccountRow } from '../../domain/customerTypes'
import { loadWebCustomerSnapshot } from '../../infra/webCustomerSnapshotCache'
import { getCustomerAmountFieldError, getCustomerChargeBlockedError, getCustomerCreditLimitConfirmation, getCustomerPaymentAmountError, getCustomerPaymentNoDebtHint } from '../../application/validateCustomerManualMovement';
import { validateCustomerCheckDrafts } from '../../application/validateCustomerCheckDrafts';
import { mapCustomerCommandError } from '../../application/mapCustomerCommandError';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { getMergedActiveCashSessionId } from '../../../cash/infra-api';
import type { CustomerPaymentMethodId } from '../../infra/customerPayments';
import { emptyCheckDraft, useCustomerOverlayStore } from '../../store/useCustomerOverlayStore';
import type { OperationalConfirmModalProps } from '../../../shared/modals/OperationalConfirmModal';
import { showError, showSuccess } from '../../../shared/ui/appToast';
import { reportUserVisibleFailure } from '../../../shared/sentry/sentryUser';
import { useSupervisorPinPrompt } from '../../../shell/session/useSupervisorPinPrompt';
import { authorizeSupervisorPin, mapSupervisorPinError } from '../../../shell/session/supervisorPinApi';
import { isOnline } from '../../../shell/connectivity/connectivityInfra';
import { pickAttachmentInteractive, type PickedAttachmentFile } from '../../../shared/attachments/infra/pickAttachmentFile';
import { saveCustomerMovementAttachment } from '../../infra/customerMovementAttachmentsRepo';
import { getSessionOrganizationId } from '../../../shell/session/sessionOrganization';
import { isUnexpectedFailureHandled } from '../../../shared/sentry/presentUnexpectedFailure';

import { MOVEMENT_PREVIEW_SIZE, CUSTOMER_HISTORY_PAGE_SIZE } from '../../domain/customerConstants';
type Params = {
  canCollect: boolean;
  canAdjustDebt: boolean;
  refreshCustomerTotalCount: () => Promise<void>;
  loadCustomers: () => Promise<void>;
  openCustomerHistory: (customer: CustomerAccountRow) => Promise<void>;
};

type PendingMovement = {
  type: 'charge' | 'payment';
  value: number;
  /** Cargo sobre límite → requiere PIN supervisor (ADR-027). */
  requiresCreditOverride: boolean;
  confirms: Array<{ title: string; message: string }>;
  confirmIndex: number;
};

type OpenMovementModalOptions = {
  saleId?: string | null;
  suggestedAmount?: number;
};

export function useCustomerMovementFlow({
  canCollect,
  canAdjustDebt,
  refreshCustomerTotalCount,
  loadCustomers,
  openCustomerHistory,
}: Params) {
  const user = useAuthStore((s) => s.user);
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const { promptPin, PinModal: SupervisorPinModal } = useSupervisorPinPrompt();
  const retryMovementIdRef = useRef<string | null>(null);
  const hasChecksCapability = useHasBusinessCapability('checks');
  const hasCashSessionsCapability = useHasBusinessCapability('cash_sessions');

  const {
    movementModal,
    selectedCustomer,
    setSelectedCustomer,
    movements,
    setMovements,
    movementLoading,
    setMovementLoading,
    movementsOffset,
    setMovementsOffset,
    movementsHasMore,
    setMovementsHasMore,
    movementsLoadingMore,
    setMovementsLoadingMore,
    amountInput,
    setAmountInput,
    noteInput,
    setNoteInput,
    paymentMethod,
    setPaymentMethod,
    checkDrafts,
    setCheckDrafts,
    updateCheckDraft,
    addCheckDraft,
    removeCheckDraft,
    closeMovementModal: closeMovementModalStore,
  } = useCustomerOverlayStore();

  const [savingMovement, setSavingMovement] = useState(false);
  const [pendingMovement, setPendingMovement] = useState<PendingMovement | null>(null);
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null);
  const [customerSalesOptions, setCustomerSalesOptions] = useState<
    Array<{ id: string; shortId: string; createdAt: string; total: number; paidAmount: number; pendingAmount: number }>
  >([]);
  const [loadingCustomerSales, setLoadingCustomerSales] = useState(false);
  const [activeCashSessionId, setActiveCashSessionId] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<PickedAttachmentFile[]>([]);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [qrIntent, setQrIntent] = useState<{ intentId: string; amount: number } | null>(null);

  const { entitlements, loading: entitlementsLoading } = usePlanEntitlements();
  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();
  const [mpPosConfigured, setMpPosConfigured] = useState<boolean>(false);

  useEffect(() => {
    let alive = true;
    setMpPosConfigured(false);
    if (!currentBranchId) {
      return () => {
        alive = false;
      };
    }
    void getCachedMpIntegrationStatus(currentBranchId)
      .then((status) => {
        if (alive) setMpPosConfigured(status.oauth_connected === true && status.pos_configured === true);
      })
      .catch(captureCatch('customers', 'mp-integration-status'));
    return () => {
      alive = false;
    };
  }, [currentBranchId]);

  const mercadopagoQrAllowed = useMemo(() => {
    if (!isGlobalFeatureOn(globalFeatureFlags, 'mercadopago_integrations')) return false;
    if (entitlementsLoading || !entitlements) return false;
    if (entitlements.mp_qr_pos !== true) return false;
    return mpPosConfigured;
  }, [entitlements, entitlementsLoading, globalFeatureFlags, mpPosConfigured]);

  const openMovementModal = useCallback(
    async (customer: CustomerAccountRow, options?: OpenMovementModalOptions) => {
      setSelectedCustomer(customer);
      useCustomerOverlayStore.getState().setMovementModal(true);
      setMovementLoading(true);
      setMovementsOffset(0);
      setMovementsHasMore(false);
      setMovementsLoadingMore(false);
      setAmountInput(
        options?.suggestedAmount != null && options.suggestedAmount > 0
          ? String(options.suggestedAmount)
          : ''
      );
      setNoteInput('');
      setSelectedSaleId(options?.saleId ?? null);
      setCustomerSalesOptions([]);
      setLoadingCustomerSales(true);
      setQrIntent(null);
      retryMovementIdRef.current = null;
      try {
        const sessionId = currentBranchId
          ? await getMergedActiveCashSessionId(currentBranchId).catch(() => null)
          : null;
        setActiveCashSessionId(sessionId);
        // Efectivo por default cuando hay caja abierta (o el módulo de caja
        // no aplica); si no, transferencia. El cajero puede cambiarlo.
        setPaymentMethod(sessionId || !hasCashSessionsCapability ? 'cash' : 'transfer');
        setCheckDrafts([emptyCheckDraft()]);
        setPendingAttachments([]);
        await loadWebCustomerSnapshot(false);
        const rows = await getCustomerMovementHistory(customer.id, MOVEMENT_PREVIEW_SIZE, 0, {});
        setMovements(rows);
        setMovementsOffset(rows.length);
        setMovementsHasMore(false);
        const sales = await getCustomerSalesForCustomer(customer.id);
        setCustomerSalesOptions(sales);
      } finally {
        setMovementLoading(false);
        setLoadingCustomerSales(false);
      }
    },
    [
      currentBranchId,
      hasCashSessionsCapability,
      setAmountInput,
      setCheckDrafts,
      setMovementLoading,
      setMovements,
      setMovementsHasMore,
      setMovementsLoadingMore,
      setMovementsOffset,
      setNoteInput,
      setPaymentMethod,
      setSelectedCustomer,
    ],
  );

  const closeMovementConfirm = useCallback(() => {
    if (savingMovement) return;
    setPendingMovement(null);
  }, [savingMovement]);

  const addMovementAttachment = useCallback(async () => {
    if (attachmentBusy) return;
    setAttachmentBusy(true);
    try {
      const picked = await pickAttachmentInteractive('customers');
      if (picked) {
        setPendingAttachments((current) => [...current, picked]);
      }
    } catch (error) {
      // Unexpected: el picker ya toasteó + reportó; los errores esperados
      // (tipo/tamaño) se muestran acá.
      if (!isUnexpectedFailureHandled(error)) {
        showError('Adjuntar comprobante', error instanceof Error ? error.message : String(error));
      }
    } finally {
      setAttachmentBusy(false);
    }
  }, [attachmentBusy]);

  const removeMovementAttachment = useCallback((index: number) => {
    setPendingAttachments((current) => current.filter((_, i) => i !== index));
  }, []);

  const openFullHistoryFromMovement = useCallback(() => {
    if (!selectedCustomer) return;
    const c = selectedCustomer;
    closeMovementModalStore();
    void openCustomerHistory(c);
  }, [selectedCustomer, closeMovementModalStore, openCustomerHistory]);

  const loadMoreMovements = useCallback(async () => {
    if (!selectedCustomer || movementsLoadingMore || !movementsHasMore) return;
    setMovementsLoadingMore(true);
    try {
      const rows = await fetchCustomerMovements(selectedCustomer.id, {
        limit: CUSTOMER_HISTORY_PAGE_SIZE,
        offset: movementsOffset,
      });
      setMovements((current) => [...current, ...rows]);
      setMovementsOffset((current) => current + rows.length);
      setMovementsHasMore(rows.length === CUSTOMER_HISTORY_PAGE_SIZE);
    } catch (error) {
      console.warn('[Customer] No se pudieron cargar más movimientos', error);
      setMovementsHasMore(false);
    } finally {
      setMovementsLoadingMore(false);
    }
  }, [movementsHasMore, movementsLoadingMore, movementsOffset, selectedCustomer, setMovements, setMovementsHasMore, setMovementsLoadingMore, setMovementsOffset]);

  /** Refresh post-cobro (efectivo, cheque o QR aprobado): cliente, historial y lista. */
  const refreshAfterPayment = useCallback(async () => {
    if (!selectedCustomer) return;
    try {
      await refreshCustomerTotalCount();
      await loadCustomers();
      const refreshedCustomer = await getCustomerAccountById(selectedCustomer.id);
      if (refreshedCustomer) {
        setSelectedCustomer(refreshedCustomer);
      }
      const rows = await getCustomerMovementHistory(selectedCustomer.id, MOVEMENT_PREVIEW_SIZE, 0, {});
      setMovements(rows);
      setMovementsOffset(rows.length);
      setMovementsHasMore(false);
      setAmountInput('');
      setNoteInput('');
      setCheckDrafts([emptyCheckDraft()]);
      setPendingAttachments([]);
      setSelectedSaleId(null);
    } catch (refreshError) {
      console.warn('[customer] UI refresh after movement failed', refreshError);
    }
  }, [
    loadCustomers,
    refreshCustomerTotalCount,
    selectedCustomer,
    setAmountInput,
    setCheckDrafts,
    setMovements,
    setMovementsHasMore,
    setMovementsOffset,
    setNoteInput,
    setSelectedCustomer,
    setSelectedSaleId,
  ]);

  // Cobro QR (Mercado Pago): el intent ya tiene el payload del cobro; acá solo
  // se espera la confirmación. Si aprueba, se aplica el cobro y refresco igual
  // que un cobro en efectivo.
  const qrSession = useCustomerPaymentQrSession({
    intentId: qrIntent?.intentId ?? null,
    onApproved: () => {
      void refreshAfterPayment().then(() => {
        showSuccess('Cobro confirmado', 'Mercado Pago confirmó el pago QR.');
      });
    },
  });

  const closeQrSession = useCallback(() => {
    // Cerrar sin cancelar: si el cliente ya escaneó, el cobro se liquida solo
    // cuando MP confirme (webhook). El intent queda pending hasta expirar.
    setQrIntent(null);
    qrSession.reset();
  }, [qrSession]);

  const cancelQrIntent = useCallback(async () => {
    const intent = qrIntent;
    setQrIntent(null);
    qrSession.reset();
    if (intent) {
      try {
        await cancelCustomerPaymentIntent(intent.intentId);
      } catch (error) {
        console.warn('[customer] cancel qr intent failed', error);
      }
    }
  }, [qrIntent, qrSession]);

  const closeMovementModal = useCallback(() => {
    closeMovementConfirm();
    closeMovementModalStore();
    retryMovementIdRef.current = null;
    setQrIntent(null);
    qrSession.reset();
  }, [closeMovementConfirm, closeMovementModalStore, qrSession]);

  const executeMovement = useCallback(
    async (type: 'charge' | 'payment', value: number, creditOverrideId?: string | null) => {
      if (!selectedCustomer) return;

      const online = await isOnline().catch(() => false);
      if (!online) {
        showError('Sin conexión', 'Necesitás internet para registrar consumos o cobros.');
        return;
      }

      setSavingMovement(true);
      const movementId = retryMovementIdRef.current ?? crypto.randomUUID();
      retryMovementIdRef.current = movementId;
      try {
        const actor = user?.id ?? null;
        if (type === 'charge') {
          await recordCustomerCharge({
            customerId: selectedCustomer.id,
            amount: value,
            note: noteInput.trim() || undefined,
            performedByUserId: actor,
            branchId: currentBranchId ?? null,
            movementId,
            creditOverrideId: creditOverrideId ?? null,
          });
        } else if (paymentMethod === 'mercadopago_qr') {
          // QR dinámico: crear el intent (guarda el cobro completo) y abrir la
          // espera de confirmación MP. No hay movimiento hasta que MP apruebe.
          const intent = await createCustomerPaymentIntent({
            customerId: selectedCustomer.id,
            amount: value,
            note: noteInput.trim() || undefined,
            branchId: currentBranchId ?? null,
            saleId: selectedSaleId,
            performedByUserId: actor,
            movementId,
          });
          retryMovementIdRef.current = null;
          setQrIntent({ intentId: intent.intentId, amount: value });
          return;
        } else {
          await recordCustomerPayment({
            customerId: selectedCustomer.id,
            amount: value,
            note: noteInput.trim() || undefined,
            performedByUserId: actor,
            branchId: currentBranchId ?? null,
            saleId: selectedSaleId,
            movementId,
            paymentMethod,
            checks:
              paymentMethod === 'cheque'
                ? checkDrafts.map((d) => ({
                    number: d.number.trim(),
                    dueDate: d.dueDate.trim() || undefined,
                    drawer: d.drawer.trim() || undefined,
                    bank: d.bank.trim() || undefined,
                    amount:
                      checkDrafts.length > 1
                        ? parseDecimalInput(d.amount, { min: 0 }) ?? undefined
                        : undefined,
                  }))
                : null,
          });
        }
        retryMovementIdRef.current = null;

        // Adjuntos (best-effort, post-effect): el cobro ya quedó registrado —
        // un fallo acá no lo convierte en fallido (regla 4.1 de commands).
        if (type === 'payment' && pendingAttachments.length > 0) {
          const orgId = await getSessionOrganizationId().catch(() => null);
          let failed = 0;
          for (const file of pendingAttachments) {
            try {
              if (!orgId) throw new Error('sin org');
              await saveCustomerMovementAttachment({
                attachmentId: crypto.randomUUID(),
                movementId,
                orgId,
                fileName: file.fileName,
                mimeType: file.mimeType,
                fileSize: file.fileSize,
                fileBase64: file.base64,
                createdBy: actor,
              });
            } catch (attachError) {
              failed += 1;
              console.warn('[customer] attachment upload failed', attachError);
            }
          }
          if (failed > 0) {
            showError(
              'Comprobante',
              failed === 1
                  ? 'El cobro se registró pero no pude adjuntar el comprobante. Podés adjuntarlo desde el historial.'
                  : `El cobro se registró pero no pude adjuntar ${failed} comprobantes. Podés adjuntarlos desde el historial.`
            );
          }
        }

        await refreshAfterPayment();
        showSuccess('Movimiento registrado', type === 'charge' ? 'Consumo registrado.' : 'Pago registrado.');
      } catch (error: unknown) {
        console.error('[customer] executeMovement failed', error);
        const raw = error instanceof Error ? error.message : String(error ?? '');
        const mapped = mapCustomerCommandError(raw);
        if (!mapped.expected) {
          reportUserVisibleFailure({
            module: 'customers',
            op: `customer.${type}`,
            error,
            title: 'No se pudo registrar el movimiento.',
            detail: mapped.code,
          });
        }
        showError('No se pudo registrar', mapped.userMessage);
      } finally {
        setSavingMovement(false);
      }
    },
    [
      checkDrafts,
      currentBranchId,
      noteInput,
      paymentMethod,
      pendingAttachments,
      refreshAfterPayment,
      selectedCustomer,
      selectedSaleId,
      setAmountInput,
      setCheckDrafts,
      setMovements,
      setMovementsHasMore,
      setMovementsOffset,
      setNoteInput,
      setSelectedCustomer,
      setSelectedSaleId,
      user?.id,
    ],
  );

  const handleRegisterMovement = useCallback(
    async (type: 'charge' | 'payment') => {
      if (type === 'charge' && !canAdjustDebt) {
        showError('Sin permiso', 'Tu perfil no puede registrar consumos manuales.');
        return;
      }
      if (type === 'payment' && !canCollect) {
        showError('Sin permiso', 'Tu perfil no puede registrar cobros.');
        return;
      }
      if (!selectedCustomer) return;

      const amountError = getCustomerAmountFieldError(amountInput);
      if (amountError) {
        showError(amountError.title, amountError.message);
        return;
      }

      const value = parseDecimalInput(amountInput, { allowNegative: true })!;
      const confirms: Array<{ title: string; message: string }> = [];
      let requiresCreditOverride = false;

      if (type === 'charge') {
        const blocked = getCustomerChargeBlockedError(selectedCustomer);
        if (blocked) {
          showError(blocked.title, blocked.message);
          return;
        }
        const limitConfirm = getCustomerCreditLimitConfirmation(selectedCustomer, value);
        if (limitConfirm) {
          requiresCreditOverride = true;
          confirms.push(limitConfirm);
        }
      } else {
        const linkedSale = selectedSaleId
          ? customerSalesOptions.find((s) => s.id === selectedSaleId)
          : null;
        if (!linkedSale && !selectedSaleId) {
          const noDebt = getCustomerPaymentNoDebtHint(selectedCustomer.balance);
          if (noDebt) {
            showError(noDebt.title, noDebt.message);
            return;
          }
        }
        // Siempre validar tope vs deuda / pendiente (también sin venta cargada aún).
        const paymentAmountError = getCustomerPaymentAmountError(
          amountInput,
          selectedCustomer.balance,
          linkedSale ?? undefined
        );
        if (paymentAmountError) {
          showError(paymentAmountError.title, paymentAmountError.message);
          return;
        }
        // Medio de cobro (Fase A): con módulo de caja activo todo cobro exige
        // turno abierto — si no, el movimiento queda con cash_session_id null y
        // no aparece en ningún arqueo (el problema del ticket del comercio).
        if (!paymentMethod) {
          showError('Medio de cobro', 'Elegí cómo te pagó el cliente.');
          return;
        }
        if (hasCashSessionsCapability && !activeCashSessionId) {
          showError('Caja cerrada', 'Abrí caja antes de registrar un cobro.');
          return;
        }
        if (paymentMethod === 'cheque') {
          const checkValidation = validateCustomerCheckDrafts(checkDrafts, value);
          if (!checkValidation.ok) {
            showError(checkValidation.title, checkValidation.message);
            return;
          }
        }
      }

      if (confirms.length > 0) {
        setPendingMovement({ type, value, requiresCreditOverride, confirms, confirmIndex: 0 });
        return;
      }

      await executeMovement(type, value);
    },
    [
      activeCashSessionId,
      amountInput,
      canCollect,
      canAdjustDebt,
      checkDrafts,
      executeMovement,
      customerSalesOptions,
      hasCashSessionsCapability,
      paymentMethod,
      selectedCustomer,
      selectedSaleId,
    ],
  );

  const confirmMovementStep = useCallback(async () => {
    if (!pendingMovement) return;
    const nextIndex = pendingMovement.confirmIndex + 1;
    if (nextIndex < pendingMovement.confirms.length) {
      setPendingMovement({ ...pendingMovement, confirmIndex: nextIndex });
      return;
    }
    const { type, value, requiresCreditOverride } = pendingMovement;
    setPendingMovement(null);

    if (requiresCreditOverride) {
      if (!currentBranchId) {
        showError('Sin sucursal', 'Seleccioná una sucursal para autorizar el crédito.');
        return;
      }
      try {
        const pin = await promptPin({
          message: 'Ingresá el PIN de supervisor para autorizar el crédito.',
        });
        if (!pin) return;
        const supervisorOverrideId = await authorizeSupervisorPin({
          branchId: currentBranchId,
          pin,
          module: 'Fiado',
          action: 'credit_override',
          context: {
            customerId: selectedCustomer?.id,
            amount: value,
          },
        });
        await executeMovement(type, value, supervisorOverrideId);
      } catch (error) {
        showError(
          'No se pudo autorizar',
          mapSupervisorPinError(error instanceof Error ? error.message : String(error))
        );
      }
      return;
    }

    await executeMovement(type, value);
  }, [currentBranchId, executeMovement, pendingMovement, promptPin, selectedCustomer?.id]);

  const movementConfirmModalProps = useMemo(() => {
    if (!pendingMovement) return null;
    const step = pendingMovement.confirms[pendingMovement.confirmIndex];
    if (!step) return null;
    const isCredit = pendingMovement.requiresCreditOverride;
    return {
      title: step.title,
      message: step.message,
      confirmLabel: savingMovement ? 'Procesando…' : isCredit ? 'Autorizar con PIN' : 'Continuar',
      loading: savingMovement,
      iconName: 'alert-circle-outline' as const,
      destructive: isCredit,
    } satisfies Omit<OperationalConfirmModalProps, 'visible' | 'presentation' | 'onClose' | 'onConfirm'>;
  }, [pendingMovement, savingMovement]);

  return {
    movementModal,
    selectedCustomer,
    movements,
    movementLoading,
    movementsHasMore,
    movementsLoadingMore,
    amountInput,
    noteInput,
    setAmountInput,
    setNoteInput,
    paymentMethod,
    setPaymentMethod,
    checkDrafts,
    updateCheckDraft,
    addCheckDraft,
    removeCheckDraft,
    hasChecksCapability,
    hasCashSessionsCapability,
    activeCashSessionId,
    pendingAttachments,
    attachmentBusy,
    addMovementAttachment,
    removeMovementAttachment,
    savingMovement,
    selectedSaleId,
    setSelectedSaleId,
    customerSalesOptions,
    loadingCustomerSales,
    openMovementModal,
    closeMovementModal,
    mercadopagoQrAllowed,
    qrIntent,
    qrSessionState: qrSession.state,
    regenerateQr: qrSession.regenerate,
    closeQrSession,
    cancelQrIntent,
    openFullHistoryFromMovement,
    loadMoreMovements,
    handleRegisterMovement,
    closeMovementConfirm,
    confirmMovementStep,
    movementConfirmModalProps,
    SupervisorPinModal,
  };
}
